import React, { useState } from "react";
// Common Component
import { DynamicDropdown, TimePicker } from "src/Components";
import DatePicker from "src/Components/DatePicker";
import Button from "src/Components/Button";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import { WrappedTimeSeriesChart } from "src/Components/Timeseries/WrappedTimeSeriesChart";
// Requests
import { getHourlyPortfoValueGraph } from "src/Requests/portfolioRequests";
// Types
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { IOrderBookGraphSearchParams } from "src/Interfaces/orderbookGraphInterfaces";
import {
  IPortfolioTableReport,
  IHourlyPortfoValueGraphDTO,
} from "src/Interfaces/portfolio";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import moment from "moment";
import { getStepInstruments } from "src/Requests/stepRequests";

interface IProps {
  row: IPortfolioTableReport;
}

const ChartUI = ({ row }: IProps) => {
  // local store
  const [loading, setLoading] = React.useState(false);
  const [graphData, setGraphData] = React.useState<Graph_Dto[]>([]);
  const [formValues, setFormValues] =
    React.useState<IOrderBookGraphSearchParams>({
      fromTime: "08:30:00",
      toTime: "13:00:00",
      date: moment().format("YYYY-MM-DD"),
    } as IOrderBookGraphSearchParams);

  const [timeISOValues, setTimeISOValues] = React.useState<{
    fromTime: string | null;
    toTime: string | null;
  }>({
    fromTime: getFromTimeISODefaultValue(),
    toTime: getToTimeISODefaultValue(),
  });
  const [instrumentId, setInstrumentId] = useState(row.instrumentId);

  React.useEffect(() => {
    setLoading(true);
    const data: IHourlyPortfoValueGraphDTO = {
      fromTime: formValues.fromTime,
      toTime: formValues.toTime,
      date: moment().format("YYYY-MM-DD"),
      userId: row.userId,
      instrumentId,
    };
    getHourlyPortfoValueGraph(data)
      .then((resp: Graph_Dto[]) => setGraphData(resp))
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }, [row]);

  function getFromTimeISODefaultValue() {
    const defaultFromTime = new Date();
    defaultFromTime.setHours(8, 30, 0, 0); // Set to 8:30 AM today

    return defaultFromTime.toISOString();
  }
  function getToTimeISODefaultValue() {
    const defaultToTime = new Date();
    defaultToTime.setHours(13, 0, 0, 0); // Set to 13:00 PM today
    return defaultToTime.toISOString();
  }

  const changeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const timePickerChangeHandler = (
    name: string,
    value: {
      timeString: string;
      ISOString: string | null;
    }
  ) => {
    setTimeISOValues({ ...timeISOValues, [name]: value.ISOString });
    setFormValues({ ...formValues, [name]: value.timeString });
  };

  const onSubmit = () => {
    setLoading(true);

    const data: IHourlyPortfoValueGraphDTO = {
      fromTime: formValues.fromTime,
      toTime: formValues.toTime,
      date: formValues.date,
      userId: row.userId,
      instrumentId,
    };
    getHourlyPortfoValueGraph(data)
      .then((resp: Graph_Dto[]) => setGraphData(resp))
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const isEnabled = React.useMemo(
    () =>
      !!timeISOValues.fromTime && !!timeISOValues.toTime && !!formValues.date,
    [formValues, timeISOValues]
  );

  return (
    <>
      <div className="h-full w-full min-h-0 flex flex-row items-stretch gap-5 px-4">
        {loading ? <FullScreenLoading /> : <></>}
        <div className="flex shrink-0 flex-col justify-start items-start gap-3">
          <DatePicker
            label="تاریخ"
            name="date"
            onChange={changeHandler}
            value={formValues.date}
          />
          <TimePicker
            name="fromTime"
            value={timeISOValues.fromTime}
            onChange={timePickerChangeHandler}
            placeholder="از ساعت"
          />
          <TimePicker
            name="toTime"
            value={timeISOValues.toTime}
            onChange={timePickerChangeHandler}
            placeholder="تا ساعت"
          />
          {row.instrumentId ? (
            <DynamicDropdown
              dropdown={{
                name: "",
                placeholder: "نماد ها",
              }}
              getData={getStepInstruments}
              onChange={(_, v) => setInstrumentId(v)}
              value={instrumentId}
            />
          ) : (
            <></>
          )}

          <Button
            text="جستجو"
            onClick={onSubmit}
            variant="contained"
            disabled={!isEnabled}
          />
        </div>
        <div className="chart-fill-modal">
          <div className="flex flex-row justify-center items-center gap-1 mx-auto font-bold">
            <div>اکانت:</div>
            <div>{row.sahraUser}</div>
          </div>
          <WrappedTimeSeriesChart dataset={graphData} YValueType="price" className="h-full w-full" />
        </div>
      </div>
    </>
  );
};

export default ChartUI;
